import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointResendEmailSubmitInput,
  type EnrollmentJointResendEmailSubmitOutput,
  type EnrollmentJointVerifyEmailData,
  type EnrollmentJointVerifyEmailSubmitInput,
  type EnrollmentJointVerifyEmailSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint VerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointVerifyEmailData>
 * @example const response = await getEnrollmentJointVerifyEmailData()
 */
export const getEnrollmentJointVerifyEmailData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointVerifyEmailData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointVerifyEmailData',
      query: `mutation getEnrollmentJointVerifyEmailData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            applicationId
            path
            progress
            email
            name {
              firstName
              lastName
            }
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointVerifyEmailData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointVerifyEmailData = createQueryHook<undefined, EnrollmentJointVerifyEmailData>(
  getEnrollmentJointVerifyEmailData,
)

/**
 * Submit Enrollment Joint VerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointVerifyEmailSubmitOutput>
 * @example const response = await submitEnrollmentJointVerifyEmailData();
 */
export const submitEnrollmentJointVerifyEmailData = async (
  enrollmentVerifyEmailSubmitInput: EnrollmentJointVerifyEmailSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointVerifyEmailSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointVerifyEmailData',
      query: `mutation submitEnrollmentJointVerifyEmailData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentVerifyEmailSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointVerifyEmailSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointVerifyEmailData = createMutationHook<
  EnrollmentJointVerifyEmailSubmitInput,
  EnrollmentJointVerifyEmailSubmitOutput
>(submitEnrollmentJointVerifyEmailData)

/**
 * Resend email - VerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointResendEmailSubmitOutput>
 * @example const response = await submitEnrollmentJointResendEmailData();
 */
export const submitEnrollmentJointResendEmailData = async (
  enrollmentResendEmailSubmitInput: EnrollmentJointResendEmailSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointResendEmailSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointResendEmailData',
      query: `mutation submitEnrollmentJointResendEmailData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            progress
          }
        }
      }`,
    },
    {
      input: enrollmentResendEmailSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointResendEmailSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointResendEmailData = createMutationHook<
  EnrollmentJointResendEmailSubmitInput,
  EnrollmentJointResendEmailSubmitOutput
>(submitEnrollmentJointResendEmailData)
